/**
 * Activity — the full cross-project feed (supervisor/admin). Notes, photos, and
 * punch items from every project, newest first, with deep links. "Load more"
 * bumps the page size.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useActivityFeed } from '@/api/activity';
import { ActivityFeedList } from '@/components/ActivityFeedList';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';

export function Activity() {
  const [limit, setLimit] = useState(20);
  const { data, isLoading, error, isFetching } = useActivityFeed(limit);

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Activity</h1>
        <p className="text-sm text-ink-dim">
          Notes, photos, and punch items from every project — newest first.
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-ink-dim py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="text-sm text-status-blocked-fg py-6 text-center" role="alert">
              {(error as Error).message}
            </div>
          )}
          {data && data.items.length === 0 && (
            <div className="text-sm text-ink-dim py-6 text-center">
              No field activity yet — notes, photos, and punch items from the crew show up here.
            </div>
          )}
          {data && data.items.length > 0 && <ActivityFeedList items={data.items} />}

          {data?.has_more && (
            <div className="mt-5 text-center">
              <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 20)} disabled={isFetching}>
                {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Load more
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
